import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useUIConfig } from '../Context';
import Toolbar from '../../../../controls/Toolbar';
import SortableAccordion from '../../../../controls/drag-n-drop/SortableAccordion';
import RenderParams from './RenderParams';
import AddItemInSortableObjectModal from '../modals/AddItemInSortableObjectModal';

const JSX_TO_DISPLAY_FIRST_COMPONENT = ['SplitPanel'];

const SortableObjectComponent = ({
    label,
    description,
    keys,
    path,
    disabled,
    initialValue,
    overridesValue
}) => {
    const { add, edit, reorder, remove, sortComponents, renderComponentForParameter, getInitialValue, getOverridesValue, reset, saveChanges, propOverrides } = useUIConfig();

    const [showAddItemModal, setShowAddItemModal] = useState(false);
    const [hiddenComponentIndices, setHiddenComponentIndices] = useState([]);
    const [visibleComponentIndices, setVisibleComponentIndices] = useState([]);
    const [data, setData] = useState([]);
    const [translate] = useTranslation();

    const onChange = useCallback((configKey, key, value) => {
        edit([...path, configKey], key, value);
    }, [edit, path]);

    const addNew = useCallback(() => {
        if (keys.find(key => key.name === 'components')) {
            setShowAddItemModal(true);
        }
        else {
            add(path, {});
        }
    }, [add, keys, path]);

    const onAddWithItem = useCallback(({ layoutItem }) => {
        if (layoutItem) {
            add(path, {
                'components': {
                    '100': {
                        'LayoutItem': { 'name': layoutItem }
                    }
                }
            });
        }
        else {
            add(path, {});
        }

        setShowAddItemModal(false);
    }, [add, path]);

    const renderId = useCallback((configKey, config) => {
        const returnValue = configKey;
        if (!JSX_TO_DISPLAY_FIRST_COMPONENT.includes(propOverrides.path)) {
            return returnValue;
        }
        const key = keys.find(o => o.name === 'components');
        const initialValue = key.initialValue || getInitialValue(config[key.name], key.defaultValue, key.type);

        if (!initialValue) {
            return returnValue;
        }

        const firstComponentName = initialValue[Object.keys(initialValue)[0]].LayoutItem.name;
        const extraComponents = Object.keys(initialValue).length > 1 ? ', ...' : '';

        return `${returnValue} - [${firstComponentName}${extraComponents}]`;
    }, [propOverrides.path, keys, getInitialValue]);

    useEffect(() => {
        if (!initialValue) {
            setHiddenComponentIndices([]);
            setVisibleComponentIndices([]);
            setData([]);
            return;
        }

        setHiddenComponentIndices([]);
        setVisibleComponentIndices([]);
        const arr = sortComponents(initialValue).map(([configKey, config], i) => {
            if (config?.Hidden) {
                setHiddenComponentIndices(prev => [...prev, i]);
            }
            else {
                setVisibleComponentIndices(prev => [...prev, i]);
            }

            return {
                _id: configKey,
                displayId: renderId(configKey, config),
                htmlContent: <div className="w-100">
                    <RenderParams
                        params={keys.map((key) => ({
                            ...key,
                            initialValue: key.initialValue || getInitialValue(config[key.name], key.defaultValue, key.type),
                            overridesValue: getOverridesValue(overridesValue, key.name, configKey),
                            configKey,
                        }))}
                        disabled={disabled}
                        currentPath={path}
                        updateCallback={onChange}
                        resetCallback={(paramName) => { reset([...path, configKey], paramName); saveChanges(); }}
                    />
                </div>
            };
        });
        setData(arr);
    }, [keys, sortComponents, initialValue, disabled, path, overridesValue, renderComponentForParameter, getInitialValue, getOverridesValue, reset, saveChanges, renderId, translate, onChange]);

    const callbacks = useCallback(() => ({
        Delete: {
            hiddenIndices: hiddenComponentIndices,
            icon: 'Delete',
            variant: 'outline-danger',
            callback: (_event, key) => remove(path, key),
        },
        ReAdd: {
            hiddenIndices: visibleComponentIndices,
            icon: 'Add',
            variant: 'outline-primary',
            callback: (_event, key) => edit([...path, key], 'Hidden', false),
        },
    }), [edit, path, remove, hiddenComponentIndices, visibleComponentIndices]);

    const renderToolbar = useMemo(() =>
        <div className="border-accenture border-bottom">
            <Toolbar
                buttons={disabled ? [] : [
                    {
                        name: 'Add',
                        onClick: addNew
                    }
                ]}
                heading={label}
                tooltip={description}
                buttonTooltip={false}
                className='h6'
            />
        </div>, [disabled, label, description, addNew]);

    return (
        <>
            <AddItemInSortableObjectModal
                show={showAddItemModal}
                keyName={label}
                onAdd={({ layoutItem }) => onAddWithItem({ layoutItem })}
                onCancel={() => { setShowAddItemModal(false) }}
            />
            {renderToolbar}
            {data.length > 0 ? <SortableAccordion
                data={data}
                attr='htmlContent'
                callbacks={disabled ? null : callbacks()}
                drag={disabled ? null : {
                    type: 'ComponentListItem',
                    onReorder: (dragIndex, hoverIndex) => reorder(path, dragIndex, hoverIndex)
                }}
            /> :
                <label className={`p-1 ps-3 w-100 border border-1 border-top-0 ${disabled ? 'disabled-background' : 'bg-body'}`}>{translate("OTM_NO_DATA")}</label>}
        </>
    );
};

export default SortableObjectComponent;